import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './utils';
import { UpdateUserPasswordPayload, MFAEnablePayload, LoginInfo, PartialUser } from 'src/dto/User';

export type UpdateUserPayload = {
  first_name: string;
  last_name: string;
  phone: string | null;
  phone_country_code: string | null;
  country?: string;
};

export const usersApi = createApi({
  reducerPath: 'usersApi',
  baseQuery: baseQuery,
  tagTypes: ['Users'],
  endpoints: (builder) => ({
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    getCurrentUser: builder.query<{ item: PartialUser }, void>({
      query: () => ({
        url: '/v1/user',
      }),
      providesTags: ['Users'],
    }),
    getLoginHistory: builder.query<LoginInfo, { username: string }>({
      query: ({ username }) => ({
        url: '/v1/user/login-info',
        params: {
          username: username,
        },
      }),
      providesTags: ['Users'],
    }),
    updateCurrentUser: builder.mutation<UpdateUserPayload, UpdateUserPayload>({
      query(data) {
        return {
          url: '/v1/user',
          method: 'PUT',
          body: data,
        };
      },
      invalidatesTags: ['Users'],
    }),
    updatePartialUser: builder.mutation<PartialUser, Partial<PartialUser> & { company_name: string }>({
      query(data) {
        return {
          url: '/v1/user/complete',
          method: 'POST',
          body: data,
        };
      },
    }),
    updateUserPassword: builder.mutation<UpdateUserPasswordPayload, UpdateUserPasswordPayload>({
      query(data) {
        return {
          url: '/v1/user/change-password',
          method: 'PUT',
          body: data,
        };
      },
    }),
    enableMFA: builder.mutation<MFAEnablePayload, MFAEnablePayload>({
      query(data) {
        return {
          url: '/v1/user/mfa/enable',
          method: 'POST',
          body: data,
        };
      },
    }),
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    disableMFA: builder.mutation<void, void>({
      query() {
        return {
          url: '/v1/user/mfa/disable',
          method: 'POST',
        };
      },
    }),
    verifyMFA: builder.mutation<{ code: string }, { code: string }>({
      query(data) {
        return {
          url: '/v1/user/mfa/verify',
          method: 'POST',
          body: data,
        };
      },
    }),
  }),
});

export const {
  useGetCurrentUserQuery,
  useLazyGetCurrentUserQuery,
  useEnableMFAMutation,
  useDisableMFAMutation,
  useVerifyMFAMutation,
  useGetLoginHistoryQuery,
  useUpdateCurrentUserMutation,
  useUpdatePartialUserMutation,
  useUpdateUserPasswordMutation,
} = usersApi;
